import {
  createTopoloJsonClient,
  type TopoloJsonClient,
  type TopoloJsonErrorContext,
} from '@topolo-io/auth-client';
import { getApiUrl } from '../../config/env';
import { CRM_COPY } from '../../lib/crm-copy';
import { reportClientError } from '../../lib/client-telemetry';
import { authClient } from '../../lib/topolo-auth';
import { buildBrowserWorkspaceResourceHeaders, type AppUser } from '@topolo-io/app-shell';
import type { ApiResponse } from '../../types/api';

export const API_ERRORS = CRM_COPY.api.errors;

// Persistence key for the selected CRM workspace. Kept in lockstep with the
// Layout's useAppWorkspace (same key) so every API request is scoped to the
// workspace the shell has selected. The server verifies the id belongs to the
// caller's org before honoring it — a header alone is never trusted.
export const CRM_WORKSPACE_STORAGE_KEY = 'topolo-crm:workspace';

// Canonical workspace resource headers (X-Topolo-Resource-Type/-ID) for the
// selected workspace. Sent on EVERY authenticated request (JSON + uploads) so
// reads/writes fail closed to the active workspace.
function crmWorkspaceResourceHeaders(): Record<string, string> {
  const accessToken = authClient.getAccessToken();
  const user =
    (authClient.tokenStorage.getUser() as AppUser | null) ||
    (accessToken ? authClient.buildUserFromAccessToken(accessToken) : null);
  return buildBrowserWorkspaceResourceHeaders({ storageKey: CRM_WORKSPACE_STORAGE_KEY, user });
}

// Base API client class
class ApiClient {
  private baseUrl: string;

  constructor() {
    const configUrl = getApiUrl();

    // For localhost development with HTTPS, use the Vite proxy instead of direct backend URL
    if (
      import.meta.env.DEV &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ) {
      this.baseUrl = '/api'; // Use Vite proxy
    } else {
      this.baseUrl = configUrl;
    }
  }

  // Public method to get base URL
  getBaseUrl(): string {
    return this.baseUrl;
  }

  // Public method to get stored tokens (disabled for cookie-mode only)
  getStoredTokens(): { accessToken: string; refreshToken: string } | null {
    return null;
  }

  // Auth is @topolo-io/auth-client's contract: the shared authenticated fetch
  // below attaches the bearer token and X-App-ID, refreshes proactively near
  // expiry, and refreshes-and-retries once on a 401. Building those headers
  // here pinned a token snapshot taken before the request. What remains is
  // CRM's own workspace scoping.
  private getAuthHeaders(): Record<string, string> {
    return { ...crmWorkspaceResourceHeaders() };
  }

  // Shared JSON client (@topolo-io/auth-client v1.3.0) dedups the fetch + error
  // prelude while KEEPING CRM's result-object contract via the opt-in
  // errorFactory (returns { success:false, error, code } instead of throwing)
  // and the 401 `auth:refresh-needed` CustomEvent via the unauthorized hook.
  // The success path stays `{ success:true, data }` (data = the raw JSON; call
  // sites unwrap `data.data` themselves). fetchImpl resolves the global fetch
  // dynamically so test stubs are honored.
  private jsonClient: TopoloJsonClient = createTopoloJsonClient({
    fetchImpl: (input, init) => authClient.createAuthenticatedFetch()(input, init),
    errorFactory: (context: TopoloJsonErrorContext): ApiResponse<never> & { __crmError: true } => {
      const data = context.payload as { message?: unknown; error?: unknown } | undefined;
      const errorMessage: string =
        (typeof data?.message === 'string' && data.message) ||
        (typeof data?.error === 'string' && data.error) ||
        (data?.error &&
        typeof data.error === 'object' &&
        typeof (data.error as { message?: unknown }).message === 'string'
          ? (data.error as { message: string }).message
          : '') ||
        API_ERRORS.generic;
      reportClientError('API request failed', {
        url: String(context.input),
        status: context.status,
        statusText: context.response.statusText,
        responseData: context.payload,
        requestBody: context.init.body,
        requestOptions: context.init,
      });
      // __crmError marks this as the errorFactory's own failure result so the
      // caller does not confuse it with a HTTP-200 body that happens to carry
      // `success:false`.
      return {
        success: false,
        error: errorMessage,
        code: context.status,
        __crmError: true,
      };
    },
    unauthorized: {
      // No retry — CRM only dispatches a refresh event (parity with prior code).
      onUnauthorized: () => {
        window.dispatchEvent(new CustomEvent('auth:refresh-needed'));
      },
    },
  });

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    // Get auth headers
    const authHeaders = this.getAuthHeaders();

    // Dev-only artificial delay (controlled via localStorage.debug_api_delay_ms)
    const delayMs = (() => {
      try {
        const raw = localStorage.getItem('debug_api_delay_ms');
        const n = raw ? parseInt(raw, 10) : 0;
        return Number.isFinite(n) && n > 0 ? n : 0;
      } catch {
        return 0;
      }
    })();
    const delayMatch = (() => {
      try {
        const raw = localStorage.getItem('debug_api_delay_match');
        return raw && raw.length > 0 ? new RegExp(raw) : null;
      } catch {
        return null;
      }
    })();

    if (delayMs && (!delayMatch || delayMatch.test(endpoint))) {
      await new Promise(r => setTimeout(r, delayMs));
    }

    try {
      // The shared client returns the parsed JSON on success and, via the
      // errorFactory above, a result-object on a non-ok response. Either way we
      // land in the success branch below unless fetch itself throws (network).
      const data = await this.jsonClient<T | (ApiResponse<never> & { __crmError?: true })>(url, {
        method: options.method,
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
          ...(options.headers as Record<string, string> | undefined),
        },
        body: options.body,
        signal: options.signal,
      });

      // The errorFactory returns a fully-formed failure ApiResponse tagged with
      // __crmError; pass it through (stripping the internal tag). Anything else
      // is a successful HTTP body, wrapped as the data of a success result —
      // even if that body itself carries `success:false` (unchanged behavior).
      if (
        data &&
        typeof data === 'object' &&
        (data as { __crmError?: unknown }).__crmError === true
      ) {
        const { __crmError, ...failure } = data as ApiResponse<never> & { __crmError: true };
        void __crmError;
        return failure as ApiResponse<T>;
      }

      return {
        success: true,
        data: data as T,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : API_ERRORS.network,
      };
    }
  }

  // GET request
  async get<T>(
    endpoint: string,
    options?: { signal?: AbortSignal; headers?: Record<string, string> }
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  // POST request
  async post<T>(endpoint: string, data: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // PUT request
  async put<T>(endpoint: string, data: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // PATCH request
  async patch<T>(endpoint: string, data: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // DELETE request
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

// Create API client instance
export const api = new ApiClient();

// Multipart uploads and blob downloads cannot go through the JSON client, so
// they use this authenticated fetch instead of a bare one: it attaches the
// bearer and X-App-ID, refreshes proactively near expiry, and refreshes and
// retries once on a 401. Building those headers by hand pinned a token
// snapshot taken before the request — on a long upload that is exactly when
// the token expires.
export const uploadFetch: typeof fetch = (input, init) =>
  authClient.createAuthenticatedFetch()(input, init);

// What remains is CRM's own workspace scoping; auth is the client's contract.
export function getUploadAuthHeaders(): Record<string, string> {
  return { ...crmWorkspaceResourceHeaders() };
}
